import type { StorefrontProductDetail } from '@erp/shared';
import { youTubeEmbedUrl, youTubeThumbUrl } from '@erp/shared';
import { ChevronDown, ChevronLeft, ChevronRight, Heart, ImageOff, Loader2, Minus, Play, Plus, Share2, ShoppingCart, X, ZoomIn } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { Badge } from '../../components/shadcn/index.js';
import { Button, RTE_CONTENT_CSS, NumberInput } from '../../components/ui/index.js';
import { useCartStore } from '../../lib/cartStore.js';
import { formatMoney } from '../../lib/money.js';
import { assetUrl, publicApi } from '../../lib/publicApi.js';
import { recordRecent } from '../../lib/recentlyViewed.js';
import { sanitizeHtml } from '../../lib/sanitizeHtml.js';
import { useWishlistStore } from '../../lib/wishlistStore.js';

import { Breadcrumbs } from './Breadcrumbs.js';
import { rememberSaving } from './cartSavings.js';
import { DiscountBadge, OriginalPrice, resolveSaving, type Saving } from './discountDisplay.js';
import { GroupPricingBadge } from './GroupPricingBadge.js';
import { ProductCard } from './ProductCard.js';
import { ProductReviews } from './ProductReviews.js';
import { RecentlyViewed } from './RecentlyViewed.js';
import { StorefrontShareModal } from './StorefrontShareModal.js';
import { ProductDetailSkeleton } from './StorefrontSkeletons.js';
import { StoreImage } from './StoreImage.js';

interface UnitOption { id: string; name: string; price: string; factor: string; saving: Saving | null }

export function StorefrontProductPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const add = useCartStore((s) => s.add);
  const toggleWish = useWishlistStore((s) => s.toggle);
  const [p, setP] = useState<StorefrontProductDetail | null>(null);
  const saved = useWishlistStore((s) => !!p && s.slug === slug && s.items.includes(p.id));
  const [status, setStatus] = useState<'loading' | 'ok' | 'notfound'>('loading');
  const [activeImg, setActiveImg] = useState(0);
  /** The gallery is showing the product's video rather than a photo. */
  const [showVideo, setShowVideo] = useState(false);
  const [qty, setQty] = useState(1);
  const [unitId, setUnitId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [origin, setOrigin] = useState('50% 50%');
  // Lightbox can FOCUS a different product (a similar one) without leaving the page.
  const [lightboxProduct, setLightboxProduct] = useState<StorefrontProductDetail | null>(null);
  const [lbImg, setLbImg] = useState(0);
  const [lbLoading, setLbLoading] = useState(false);

  useEffect(() => {
    setStatus('loading'); setActiveImg(0); setShowVideo(false); setQty(1); setUnitId(''); setVariantId(''); setZoomOpen(false); setZoomed(false); setLightboxProduct(null);
    void publicApi().get<StorefrontProductDetail>(`/${slug}/products/${id}`)
      .then((r) => {
        const firstSellable = r.data.variants.find((v) => v.inStock) ?? r.data.variants[0];
        setP(r.data); setVariantId(firstSellable?.id ?? ''); setStatus('ok');
        recordRecent(slug, {
          id: r.data.id, slug: r.data.slug, name: r.data.name, description: null,
          price: r.data.price, originalPrice: r.data.originalPrice, discountPercent: r.data.discountPercent,
          currencyCode: r.data.currencyCode, unit: r.data.unitOfMeasure,
          imageUrl: r.data.images[0] ?? null, inStock: r.data.inStock,
          lowStock: false, featured: false, isNew: false,
        });
        // Fire-and-forget: bump the "most viewed" counter (best-effort analytics).
        void publicApi().post(`/${slug}/products/${r.data.id}/view`).catch(() => {});
      })
      .catch(() => setStatus('notfound'));
  }, [slug, id, i18n.language]);

  // Lightbox: lock body scroll, ESC to close, arrow keys to navigate the gallery.
  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      const len = (lightboxProduct ?? p)?.images.length ?? 0;
      if (e.key === 'Escape') setZoomOpen(false);
      else if (e.key === 'ArrowRight' && len > 1) { setLbImg((i) => (i + 1) % len); setZoomed(false); }
      else if (e.key === 'ArrowLeft' && len > 1) { setLbImg((i) => (i - 1 + len) % len); setZoomed(false); }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [zoomOpen, p, lightboxProduct]);

  // Base unit + any pack/carton units, each with a resolved display price.
  const unitOptions = useMemo<UnitOption[]>(() => {
    if (!p) return [];
    const base: UnitOption = { id: '', name: p.unitOfMeasure, price: p.price, factor: '1', saving: resolveSaving(p) };
    const extra = p.units.map((u) => {
      // A unit with no price of its own falls back to the item price — and then
      // so must its saving, or it would advertise a discount from the wrong row.
      const ownPrice = !!u.price && Number(u.price) > 0;
      return {
        id: u.id,
        name: u.name,
        price: ownPrice ? u.price : p.price,
        factor: u.factor,
        saving: ownPrice ? resolveSaving(u) : resolveSaving(p),
      };
    });
    return [base, ...extra];
  }, [p]);

  if (status === 'loading') return <ProductDetailSkeleton />;
  if (status === 'notfound' || !p) return <div className="py-16 text-center text-fg-muted">{t('store.product.notFound')}</div>;

  const main = assetUrl(p.images[activeImg] ?? p.images[0] ?? null);
  // Social-share image: the product's primary image, falling back to the store logo.
  const ogImage = assetUrl(p.images[0] ?? null) ?? assetUrl(site.logoUrl);
  const ogTitle = p.seoTitle ?? p.name;
  const selected = unitOptions.find((u) => u.id === unitId) ?? { id: '', name: p.unitOfMeasure, price: p.price, factor: '1', saving: resolveSaving(p) };
  // Option/variant (colour/size) and packaging unit are INDEPENDENT — a shopper
  // can pick both. When both are chosen the option sets the per-piece price and
  // the packaging unit multiplies it by its factor (mirrors checkout pricing).
  const selectedVariant = p.variants.find((v) => v.id === variantId);
  // Option + packaging unit is the one combination this page must NOT work out
  // for itself: checkout multiplies the option's RAW price by the factor and then
  // applies any discount once, on the wholesale axis. Multiplying the option's
  // already-discounted per-piece price here would take a fixed amount off
  // factor-many times, and no client can tell that a retail-only rule doesn't
  // reach a pack — it never sees the rule. The server now sends the resolved
  // (variant × unit) price, so we just read it.
  const variantPack = selectedVariant && selected.id
    ? selectedVariant.unitPrices?.find((up) => up.unitId === selected.id)
    : undefined;
  const displayPrice = selectedVariant
    ? (variantPack ? variantPack.price : selectedVariant.price)
    : selected.price;
  // The saving follows whatever set the shown price — each row carries its own
  // original/percent, so nothing here is ever scaled or re-derived.
  const displaySaving = variantPack
    ? resolveSaving(variantPack)
    : selectedVariant ? resolveSaving(selectedVariant) : selected.saving;
  const canBuy = selectedVariant ? selectedVariant.inStock : p.inStock;
  const money = (v: string): string => formatMoney(v, { locale: i18n.language, currency: p.currencyCode });
  // "1 pack = 12 pieces" helpers. baseUnitName = the item's own (single) unit; a
  // pack/carton unit's factor says how many singles it holds, and per-piece is
  // the shown price split across them.
  const baseUnitName = p.unitOfMeasure || t('store.product.piece', { defaultValue: 'pc' });
  const perPiece = (priceStr: string, factor: string): string =>
    money(String(Number(priceStr) / (Number(factor) || 1)));

  const addToCart = (): void => {
    const parts = [p.name];
    if (selectedVariant) parts.push(selectedVariant.name);
    if (selected.id) parts.push(selected.name);
    const name = parts.join(' — ');
    add(slug, {
      itemId: p.id,
      ...(selected.id ? { itemUnitId: selected.id } : {}),
      ...(selectedVariant ? { itemVariantId: selectedVariant.id } : {}),
      name,
      price: displayPrice,
      currencyCode: p.currencyCode,
      imageUrl: p.images[0] ?? null,
      quantity: qty,
    });
    // Keep the "was" price with the line — the cart stores only what is charged.
    rememberSaving(
      slug,
      {
        itemId: p.id,
        ...(selected.id ? { itemUnitId: selected.id } : {}),
        ...(selectedVariant ? { itemVariantId: selectedVariant.id } : {}),
        price: displayPrice,
      },
      displaySaving,
    );
    toast.success(t('cart.addedTitle'), {
      description: name,
      action: { label: t('cart.viewCart'), onClick: () => navigate(`/s/${slug}/cart`) },
    });
  };

  // ---- Lightbox: focus either the page's product or a swapped-in similar one ----
  const lb = lightboxProduct ?? p;
  const lbIsOriginal = !lightboxProduct;
  const lbMain = assetUrl(lb.images[lbImg] ?? lb.images[0] ?? null);
  // The focused product's saving — the page's own (option/unit aware) when the
  // lightbox still shows this product, else the swapped-in one's plain price.
  const lbSaving = lbIsOriginal ? displaySaving : resolveSaving(lb);
  const openLightbox = (): void => { setLightboxProduct(null); setLbImg(activeImg); setZoomed(false); setZoomOpen(true); };
  const focusInLightbox = (idOrSlug: string): void => {
    setLbLoading(true);
    void publicApi().get<StorefrontProductDetail>(`/${slug}/products/${idOrSlug}`)
      .then((r) => { setLightboxProduct(r.data); setLbImg(0); setZoomed(false); })
      .catch(() => undefined)
      .finally(() => setLbLoading(false));
  };
  const addLbToCart = (): void => {
    if (lbIsOriginal) { addToCart(); return; }
    add(slug, { itemId: lb.id, name: lb.name, price: lb.price, currencyCode: lb.currencyCode, imageUrl: lb.images[0] ?? null, quantity: 1 });
    rememberSaving(slug, { itemId: lb.id, price: lb.price }, resolveSaving(lb));
    toast.success(t('cart.addedTitle'), { description: lb.name, action: { label: t('cart.viewCart'), onClick: () => navigate(`/s/${slug}/cart`) } });
  };
  const shareProduct = lbIsOriginal
    ? { id: p.id, slug: p.slug, name: p.name, price: displayPrice, currencyCode: p.currencyCode, imageUrl: p.images[0] ?? null }
    : { id: lb.id, slug: lb.slug, name: lb.name, price: lb.price, currencyCode: lb.currencyCode, imageUrl: lb.images[0] ?? null };
  // The lightbox browse strip: the original product + its related — flip focus
  // between them without leaving the viewer (the original stays the anchor).
  const lbBrowse = [
    // Use the variant-adjusted price for the original so the strip matches the main view.
    { id: p.id, slug: p.slug, name: p.name, price: displayPrice, currencyCode: p.currencyCode, imageUrl: p.images[0] ?? null },
    ...p.relatedProducts.map((rp) => ({ id: rp.id, slug: rp.slug, name: rp.name, price: rp.price, currencyCode: rp.currencyCode, imageUrl: rp.imageUrl })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <style>{RTE_CONTENT_CSS}</style>
      <Helmet>
        <title>{`${ogTitle} · ${site.storeName}`}</title>
        {p.seoDescription && <meta name="description" content={p.seoDescription} />}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={ogTitle} />
        {p.seoDescription && <meta property="og:description" content={p.seoDescription} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={ogTitle} />
        {p.seoDescription && <meta name="twitter:description" content={p.seoDescription} />}
      </Helmet>
      <Breadcrumbs
        items={[
          { label: t('store.home.crumb'), to: `/s/${slug}` },
          { label: t('store.shop.title'), to: `/s/${slug}/shop` },
          ...(p.categoryName ? [p.categorySlug ? { label: p.categoryName, to: `/s/${slug}/category/${p.categorySlug}` } : { label: p.categoryName }] : []),
          { label: p.name },
        ]}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          {/* The video plays IN the gallery, where a shopper is already looking.
              It used to sit in a section under the specs, which on a phone is
              several screens down — a video nobody scrolls to may as well not be
              there. The player mounts only once the tile is tapped, so a product
              page still costs one image. */}
          {showVideo && p.videoId ? (
            <div className="relative aspect-square overflow-hidden rounded-xl border border-border bg-black">
              <iframe
                src={youTubeEmbedUrl(p.videoId, { autoplay: true })}
                title={t('store.product.video', { defaultValue: 'Product video' })}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 m-auto aspect-video w-full"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={openLightbox}
              className="group relative aspect-square overflow-hidden rounded-xl border border-border bg-bg-subtle"
              aria-label={t('store.product.zoom', { defaultValue: 'Zoom image' })}
            >
              <StoreImage src={p.images[activeImg] ?? p.images[0] ?? null} alt={p.name} className="h-full w-full object-contain transition-transform duration-300 ease-out group-hover:scale-105 motion-reduce:transform-none" />
              {main && (
                <span className="absolute end-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-bg/70 text-fg shadow-sm backdrop-blur transition group-hover:bg-bg/90" aria-hidden>
                  <ZoomIn className="h-4 w-4" />
                </span>
              )}
            </button>
          )}
          {(p.images.length > 1 || p.videoId) && (
            <div className="flex flex-wrap gap-2">
              {p.images.map((img, i) => (
                <button key={img} type="button" onClick={() => { setActiveImg(i); setShowVideo(false); }} className={`h-16 w-16 overflow-hidden rounded-md border bg-bg-subtle ${i === activeImg && !showVideo ? 'border-brand' : 'border-border'}`}>
                  <StoreImage src={img} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
              {p.videoId && (
                <button
                  type="button"
                  onClick={() => setShowVideo(true)}
                  className={`relative h-16 w-16 overflow-hidden rounded-md border bg-black ${showVideo ? 'border-brand' : 'border-border'}`}
                  title={t('store.product.video', { defaultValue: 'Product video' })}
                  aria-label={t('store.product.video', { defaultValue: 'Product video' })}
                >
                  {/* YouTube's own poster frame — no upload, no storage, and it
                      tells the shopper what the video is before they commit. */}
                  <img src={youTubeThumbUrl(p.videoId)} alt="" className="h-full w-full object-cover opacity-85" />
                  <span className="absolute inset-0 grid place-items-center" aria-hidden>
                    <Play className="h-5 w-5 fill-white text-white drop-shadow" />
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{p.name}</h1>
            {/* Same line as the app's product page, so a shopper quoting a code
                over the phone reads the same thing on either surface. `dir="ltr"`
                because an alphanumeric code reorders inside an Arabic paragraph. */}
            {p.code && (
              <div className="mt-1 text-sm text-fg-muted">
                {t('store.product.code')}{' '}
                <span dir="ltr" className="font-mono">{p.code}</span>
              </div>
            )}
            {p.categoryName && <div className="mt-1 text-sm text-fg-muted">{p.categoryName}</div>}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-2xl font-bold tabular-nums">{money(displayPrice)}</span>
            {displaySaving && (
              <>
                <OriginalPrice amount={displaySaving.originalPrice} currency={p.currencyCode} className="font-mono text-base tabular-nums text-fg-subtle" />
                <DiscountBadge percent={displaySaving.percent} className="sf-pop rounded-full bg-danger px-2.5 py-1 text-xs font-bold text-fg-inverse shadow-sm" />
              </>
            )}
            <span className="text-sm text-fg-muted">/ {[selectedVariant?.name, selected.id ? selected.name : null].filter(Boolean).join(' · ') || selected.name}</span>
            {canBuy ? <Badge variant="success">{t('store.product.inStock')}</Badge> : <Badge variant="secondary">{t('store.product.outOfStock')}</Badge>}
            {/* Prices on this page are the shopper's own group prices when they're signed in on one. */}
            <GroupPricingBadge />
          </div>

          {Number(selected.factor) !== 1 && (
            <div className="-mt-2 text-sm text-fg-muted">
              <span className="font-medium text-fg">
                {t('store.product.equals', { unit: selected.name, n: Number(selected.factor), base: baseUnitName })}
              </span>
              {' · '}
              {t('store.product.perBase', { price: perPiece(displayPrice, selected.factor), base: baseUnitName })}
            </div>
          )}

          {p.variants.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">{t('store.product.variant', { defaultValue: 'Option' })}</span>
              <div className="flex flex-wrap gap-2">
                {p.variants.map((v) => {
                  const vSaving = resolveSaving(v);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setVariantId(v.id)}
                      disabled={!v.inStock}
                      className={`rounded-lg border px-3 py-1.5 text-sm transition disabled:cursor-not-allowed disabled:opacity-40 ${v.id === variantId ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:text-fg'}`}
                    >
                      {v.name} <span className="font-mono text-xs tabular-nums">· {money(v.price)}</span>
                      {vSaving && <OriginalPrice amount={vSaving.originalPrice} currency={p.currencyCode} className="ms-1 font-mono text-[0.7rem] tabular-nums" />}
                      {!v.inStock && <span className="ms-1 text-xs">({t('store.product.outOfStock')})</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {unitOptions.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">{t('store.product.unit')}</span>
              <div className="flex flex-wrap gap-2">
                {unitOptions.map((u) => (
                  <button
                    key={u.id || 'base'}
                    type="button"
                    onClick={() => setUnitId(u.id)}
                    className={`flex flex-col items-start gap-0.5 rounded-lg border px-3 py-1.5 text-start text-sm transition ${u.id === selected.id ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:text-fg'}`}
                  >
                    <span>
                      {u.name} <span className="font-mono text-xs tabular-nums">· {money(u.price)}</span>
                      {u.saving && <OriginalPrice amount={u.saving.originalPrice} currency={p.currencyCode} className="ms-1 font-mono text-[0.7rem] tabular-nums" />}
                    </span>
                    {Number(u.factor) !== 1 && (
                      <span className="text-[11px] opacity-70">
                        ×{Number(u.factor)} · {t('store.product.perBase', { price: perPiece(u.price, u.factor), base: baseUnitName })}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex w-full items-center gap-3 sm:w-auto sm:flex-1">
            <div className="inline-flex shrink-0 items-center rounded-lg border border-border">
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-10 w-10 place-items-center text-fg-muted hover:text-fg disabled:opacity-40" disabled={qty <= 1} aria-label={t('cart.decrease')}>
                <Minus className="h-4 w-4" aria-hidden />
              </button>
              <NumberInput
                value={String(qty)}
                onValueChange={(raw) => { const n = Math.floor(Number(raw)); setQty(Number.isFinite(n) && n >= 1 ? Math.min(n, 100000) : 1); }}
                unstyled
                className="h-10 w-14 border-x border-border bg-bg text-center text-sm tabular-nums outline-none"
                aria-label={t('cart.quantity')}
              />
              <button type="button" onClick={() => setQty((q) => Math.min(100000, q + 1))} className="grid h-10 w-10 place-items-center text-fg-muted hover:text-fg" aria-label={t('cart.increase')}>
                <Plus className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <Button onClick={addToCart} size="lg" variant="accent" disabled={!canBuy} className="flex-1 whitespace-nowrap">
              <ShoppingCart className="h-4 w-4" aria-hidden />
              {t('cart.add')}
            </Button>
            </div>
            <div className="flex items-center gap-2">
            {site.wishlistEnabled && (
              <button
                type="button"
                onClick={() => toggleWish(slug, p.id)}
                aria-label={t('store.wishlist.toggle', { defaultValue: 'Save to wishlist' })}
                aria-pressed={saved}
                className="grid h-11 w-11 flex-none place-items-center rounded-md border border-border text-fg-muted transition-[transform,color,border-color] duration-150 ease-out hover:border-danger/40 hover:text-danger active:scale-95"
              >
                <Heart className={`h-5 w-5 ${saved ? 'fill-danger text-danger' : ''}`} aria-hidden />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              aria-label={t('store.product.share', { defaultValue: 'Share' })}
              title={t('store.product.share', { defaultValue: 'Share' })}
              className="grid h-11 w-11 flex-none place-items-center rounded-md border border-border text-fg-muted transition-[transform,color,border-color] duration-150 ease-out hover:border-brand/40 hover:text-brand active:scale-95"
            >
              <Share2 className="h-5 w-5" aria-hidden />
            </button>
            </div>
          </div>

          <div className="flex flex-col divide-y divide-border border-y border-border">
            {p.descriptionHtml && (
              <details open className="group py-3">
                <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                  {t('store.product.details')}
                  <ChevronDown className="h-4 w-4 text-fg-muted transition group-open:rotate-180" aria-hidden />
                </summary>
                <div className="rte-content mt-3 text-sm text-fg-muted" dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.descriptionHtml) }} />
              </details>
            )}
            <details className="group py-3">
              <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                {t('store.product.specs')}
                <ChevronDown className="h-4 w-4 text-fg-muted transition group-open:rotate-180" aria-hidden />
              </summary>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-fg-muted">{t('store.product.uom')}</dt><dd>{p.unitOfMeasure}</dd></div>
                {p.categoryName && <div className="flex justify-between gap-4"><dt className="text-fg-muted">{t('store.product.category')}</dt><dd>{p.categoryName}</dd></div>}
                {/* The shop's own fields. Already filtered server-side to the
                    ones marked visible to customers and to those with a value,
                    so there is nothing to decide here. */}
                {(p.customFields ?? []).map((f) => (
                  <div key={f.code} className="flex justify-between gap-4">
                    <dt className="text-fg-muted">{f.label}</dt>
                    <dd className="text-end">{f.value}</dd>
                  </div>
                ))}
                {p.units.map((u) => (
                  <div key={u.id} className="flex justify-between gap-4">
                    <dt className="text-fg-muted">{u.name} <span className="text-xs">(×{u.factor})</span></dt>
                    <dd className="font-mono tabular-nums">{u.price && Number(u.price) > 0 ? money(u.price) : '—'}</dd>
                  </div>
                ))}
              </dl>
            </details>
          </div>
        </div>
      </div>

      {site.reviewsEnabled && <ProductReviews slug={slug} itemId={p.id} />}

      {p.relatedProducts.length > 0 && (
        <section className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold">{t('store.product.related')}</h2>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
            {p.relatedProducts.map((rp) => <ProductCard key={rp.id} product={rp} />)}
          </div>
        </section>
      )}

      <RecentlyViewed excludeId={p.id} />

      <StorefrontShareModal
        slug={slug}
        product={shareProduct}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />

      {zoomOpen && (
        <div
          className="fixed inset-0 z-[60] flex flex-col bg-black/95 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={lb.name}
          onClick={() => setZoomOpen(false)}
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3 text-white/90">
            <span className="min-w-0 truncate text-sm font-medium">{lb.name}</span>
            <div className="flex items-center gap-3">
              <span className="text-sm tabular-nums">{lbImg + 1} / {lb.images.length}</span>
              <button type="button" onClick={() => setZoomOpen(false)} className="grid h-10 w-10 place-items-center rounded-full bg-white/10 transition hover:bg-white/20" aria-label={t('store.product.close', { defaultValue: 'Close' })}>
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col lg:flex-row" onClick={(e) => e.stopPropagation()}>
            {/* Details (left) */}
            <aside className="order-2 w-full shrink-0 space-y-3 overflow-y-auto p-5 text-white lg:order-1 lg:w-80 lg:border-e lg:border-white/10">
              <h2 className="text-lg font-semibold leading-snug">{lb.name}</h2>
              <p className="font-mono text-xl font-bold tabular-nums">
                {lbIsOriginal ? money(displayPrice) : money(lb.price)}
                {lbSaving && <OriginalPrice amount={lbSaving.originalPrice} currency={lb.currencyCode} className="ms-2 text-sm font-normal text-white/50" />}
                <span className="ms-1 text-sm font-normal text-white/60">/ {lbIsOriginal ? (selectedVariant ? selectedVariant.name : selected.name) : lb.unitOfMeasure}</span>
              </p>
              {lb.seoDescription && <p className="line-clamp-6 text-sm leading-relaxed text-white/70">{lb.seoDescription}</p>}
              {(lbIsOriginal ? canBuy : lb.inStock) ? (
                <button type="button" onClick={addLbToCart} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-brand-contrast transition hover:opacity-90 active:scale-95">
                  <ShoppingCart className="h-4 w-4" aria-hidden />{t('store.product.addToCart', { defaultValue: 'Add to cart' })}
                </button>
              ) : (
                <p className="text-sm text-white/60">{t('store.product.outOfStock')}</p>
              )}
              <button type="button" onClick={() => setShareOpen(true)} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 px-4 py-2.5 text-sm font-medium text-white/80 transition hover:bg-white/10">
                <Share2 className="h-4 w-4" aria-hidden />{t('store.product.share', { defaultValue: 'Share' })}
              </button>
              {!lbIsOriginal && (
                <button type="button" onClick={() => { setZoomOpen(false); navigate(`/s/${slug}/product/${lb.slug ?? lb.id}`); }} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 px-4 py-2.5 text-sm font-medium text-white/80 transition hover:bg-white/10">
                  {t('store.product.viewProduct', { defaultValue: 'View full product' })}
                </button>
              )}
            </aside>

            {/* Image (right) — focused product's photos */}
            <div className="relative order-1 flex flex-1 items-center justify-center overflow-hidden p-4 lg:order-2">
              {lb.images.length > 1 && (
                <button type="button" onClick={() => { setLbImg((i) => (i - 1 + lb.images.length) % lb.images.length); setZoomed(false); }} className="absolute start-2 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20" aria-label={t('store.shop.prev', { defaultValue: 'Previous' })}>
                  <ChevronLeft className="h-6 w-6 rtl:rotate-180" aria-hidden />
                </button>
              )}
              {lbMain ? (
                <img
                  src={lbMain}
                  alt={lb.name}
                  onClick={() => setZoomed((z) => !z)}
                  onMouseMove={zoomed ? (e) => { const r = e.currentTarget.getBoundingClientRect(); setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`); } : undefined}
                  style={{ transformOrigin: origin }}
                  className={`max-h-full max-w-full select-none object-contain transition-transform duration-200 ${zoomed ? 'scale-[2.2] cursor-zoom-out' : 'cursor-zoom-in'}`}
                  draggable={false}
                />
              ) : (
                <div className="grid h-full w-full place-items-center text-white/25"><ImageOff className="h-16 w-16" aria-hidden /></div>
              )}
              {lb.images.length > 1 && (
                <button type="button" onClick={() => { setLbImg((i) => (i + 1) % lb.images.length); setZoomed(false); }} className="absolute end-2 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20" aria-label={t('store.shop.next', { defaultValue: 'Next' })}>
                  <ChevronRight className="h-6 w-6 rtl:rotate-180" aria-hidden />
                </button>
              )}
              {lbLoading && (
                <div className="absolute inset-0 grid place-items-center bg-black/40 backdrop-blur-sm">
                  <Loader2 className="h-8 w-8 animate-spin text-white/80" aria-hidden />
                </div>
              )}
            </div>
          </div>

          {/* Thumbnails (focused product) + product browser */}
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            {lb.images.length > 1 && (
              <div className="flex justify-center gap-2 overflow-x-auto px-4 py-3">
                {lb.images.map((img, i) => (
                  <button key={img} type="button" onClick={() => { setLbImg(i); setZoomed(false); }} className={`h-14 w-14 flex-none overflow-hidden rounded-md border-2 bg-white/5 transition ${i === lbImg ? 'border-white' : 'border-white/20 hover:border-white/50'}`}>
                    <StoreImage src={img} alt="" className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
            {lbBrowse.length > 1 && (
              <div className="border-t border-white/10 px-4 py-3">
                <p className="mb-2 text-[0.7rem] font-bold uppercase tracking-wider text-white/50">{t('store.product.related', { defaultValue: 'Similar products' })}</p>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {lbBrowse.map((rp) => {
                    const focused = rp.id === lb.id;
                    return (
                      <button
                        key={rp.id}
                        type="button"
                        onClick={() => { setZoomed(false); if (rp.id === p.id) { setLightboxProduct(null); setLbImg(0); } else if (!focused) focusInLightbox(rp.slug ?? rp.id); }}
                        aria-current={focused}
                        className="group flex w-24 flex-none flex-col gap-1 text-start"
                      >
                        <div className={`aspect-square overflow-hidden rounded-lg border-2 bg-white/5 transition ${focused ? 'border-white ring-2 ring-white/40' : 'border-white/15 group-hover:border-white/40'}`}>
                          <StoreImage src={rp.imageUrl} alt={rp.name} className="h-full w-full object-contain transition group-hover:scale-105" />
                        </div>
                        <span className={`line-clamp-1 text-[0.7rem] ${focused ? 'font-semibold text-white' : 'text-white/70 group-hover:text-white'}`}>{rp.name}</span>
                        <span className="font-mono text-[0.7rem] font-bold tabular-nums text-white/90">{money(rp.price)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
